<template>
  <AppWithLoading :fetch="fetchAll" v-slot:default="{ localLoading }">
    <div class="mt-5" v-if="!localLoading">
      <VCard width="900">
        <VCardTitle>
          <div class="plan-title">
            <RouterLink to="/settings/payment/plans" class="plan-title__link">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="black" />
              </svg>
            </RouterLink>
            <span>{{ $t('buyQuota') }} {{ $t(itemType) }}</span>
          </div>
        </VCardTitle>
        <VCardText>
          <div class="mb-3">
            {{ $t('rules', {type: $t(itemType)} ) }}
          </div>
          <VTextField
            v-model="count"
            type="number"
            outlined
            :label="$t('quantity')"
          />
          <div class="mb-3">
            {{ $t('payable') }} {{ calcPrice }}
          </div>
          <QuotaPayment :count="parseInt(count)" :quota-type="itemType" />
        </VCardText>
      </VCard>
    </div>
  </AppWithLoading>
</template>

<script>
import {
  computed, ref, toRefs, watch,
} from '@vue/composition-api';
import QuotaPayment from '@/components/Payment/Payment/QuotaPayment';
import { quotaItemTypes } from '@/enums';
import AppWithLoading from '@/components/AppWithLoading';
import useQuotaPrice from '@/compositions/accounting/useQuotaPrice';
import useNumberFormatter from '@/compositions/useNumberFormatter';
import { useI18n } from 'vue-i18n-composable';

export default {
  name: 'OrderQuota',
  components: { AppWithLoading, QuotaPayment },
  props: {
    type: {
      type: String,
      required: true,
    },
    itemType: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { itemType } = toRefs(props);
    const { formatCurrency } = useNumberFormatter();
    const { quotaPrices, fetchAll, getQuotaByType } = useQuotaPrice();
    const { locale } = useI18n();
    const quotaPrice = computed(() => getQuotaByType(itemType.value));

    const minCallEnCount = 25;
    const minSmsEnCount = 50;
    const defaultMinCount = 1;
    const defaultRuCount = 10;
    const ruMaxCount = 10000;
    const enMaxCount = 100000;

    const count = ref(null);

    if (locale.value === 'en') {
      switch (itemType.value) {
        case quotaItemTypes.CALLS: {
          count.value = minCallEnCount;
          break;
        }
        case quotaItemTypes.SMS: {
          count.value = minSmsEnCount;
          break;
        }
        default: {
          count.value = minSmsEnCount;
          break;
        }
      }
    } else {
      count.value = defaultRuCount;
    }

    const price = computed(() => {
      if (!quotaPrice.value) {
        return null;
      }

      return quotaPrice.value.value;
    });
    const currency = computed(() => quotaPrice?.value?.currency);

    const calcPrice = computed(() => {
      const result = count.value * (quotaPrice?.value?.value || 0);

      return formatCurrency(
        result.toFixed(2),
        currency.value,
      );
    });

    function checkAndChangeCountValueToTheMin(value) {
      if (quotaPrice?.value?.currency === 'USD') {
        switch (itemType.value) {
          case quotaItemTypes.CALLS: {
            if (value < minCallEnCount) {
              count.value = minCallEnCount;
            }
            break;
          }
          case quotaItemTypes.SMS: {
            if (value < minSmsEnCount) {
              count.value = minSmsEnCount;
            }
            break;
          }
          default: {
            break;
          }
        }

        if (count.value > enMaxCount) {
          count.value = enMaxCount;
        }
      } else {
        if (count.value < defaultMinCount) {
          count.value = defaultMinCount;
        }

        if (count.value > ruMaxCount) {
          count.value = ruMaxCount;
        }
      }
    }

    watch(count, (newValue) => {
      if (quotaPrice?.value?.currency === 'USD') {
        checkAndChangeCountValueToTheMin(newValue);
      }
    });

    return {
      count,
      calcPrice,
      price,
      quotaItemTypes,
      fetchAll,
      quotaPrices,
      quotaPrice,
    };
  },
};
</script>

<style lang="scss" scoped>
.plan-title {
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  line-height: 1.2;
  color: #212121;
  margin-bottom: 0.9375rem;
  &__link {
    width: 1.5rem;
    height: 1.5rem;
    margin-right: 0.5rem;
    svg {
      max-width: 100%;
      max-height: 100%;
    }
  }
}
</style>

<i18n lang="yml" locale="ru">
  buyQuota: Покупка
  interviews: откликов
  sms: SMS
  calls: звонков
  rules: При покупке {type} они не сгорают в начале отчетного периода
  quantity: Количество
  payable: 'К оплате:'
</i18n>
<i18n lang="yml" locale="en">
  buyQuota: Buy
  interviews: candidates
  sms: SMS
  calls: calls
  rules: If you buy {type}, they are not burned at the beginning of the reporting period
  quantity: Quantity
  payable: 'Payable:'
</i18n>
